import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { signIn } from '@src/api/user-api'
import PageLoader from '@src/components/Loader/PageLoader'
import {
  CACHED_USER_TOKEN,
  JWT_LOCAL_STORAGE_KEY,
  loadUserFromJwtString,
  UserContext,
} from '@src/contexts/user-context'
import { protectedResources } from '@src/services/Authentification'
import { reportErrorToConsole } from '@src/services/error-logger'
import type { User } from '@src/types/user'
import { useContext, useEffect, useMemo, useState, type JSX } from 'react'

interface Props {
  children: JSX.Element
}

const CACHED_USER = loadUserFromJwtString(CACHED_USER_TOKEN)

const UserProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(CACHED_USER)
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      if (!isAuthenticated) {
        instance.loginRedirect({ scopes: protectedResources.getTokenAzure.scopes.read }).catch(reportErrorToConsole)
      } else if (user === null) {
        const activeAccount = accounts[0]
        instance.setActiveAccount(activeAccount)
        instance
          .acquireTokenSilent({ account: activeAccount, scopes: protectedResources.getTokenAzure.scopes.read })
          .then((response) => {
            return signIn({ jwt: response.accessToken }).then(({ data }) => {
              const parsedUser = loadUserFromJwtString(data?.jwt)
              if (!parsedUser)
                return instance.loginRedirect({
                  account: activeAccount,
                  scopes: protectedResources.getTokenAzure.scopes.read,
                })
              setUser(parsedUser)
              localStorage.setItem(JWT_LOCAL_STORAGE_KEY, data.jwt)
              return null
            })
          })
          .catch((error) => {
            reportErrorToConsole(error)
            return instance.loginRedirect({
              account: activeAccount,
              scopes: protectedResources.getTokenAzure.scopes.read,
            })
          })
      }
    }
  }, [inProgress, isAuthenticated, user, instance, accounts])

  const ctx = useMemo(() => (user ? { user } : null), [user])
  if (inProgress !== InteractionStatus.None || !accounts.length || user === null) return <PageLoader />
  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>
}

export const useUser = () => {
  const ctx = useContext(UserContext)
  return ctx?.user
}

export default UserProvider
